/**
 * Onboarding Progress Tracker Component
 *
 * Handles analytics tracking for onboarding events via GA4.
 * Integrates with existing analytics infrastructure.
 *
 * @author Andres Nava
 * @version 1.0.0
 */

import React, { useImperativeHandle, forwardRef, useRef } from "react";
import { trackEvent } from "../../utils/analytics";
import { useAuth } from "../../contexts/auth/authContext";
import { logger } from "../../utils/logger";
import type { TourAnalyticsEvent } from "../../types/onboarding";
import { shouldLogOnboardingDebug } from "./onboardingEventNames";

/**
 * Onboarding Progress Tracker Props
 */
interface OnboardingProgressTrackerProps {
  /** Tour name for analytics */
  tourName?: string;
  /** User role for analytics */
  userRole?: string;
}

/**
 * Onboarding Progress Tracker Ref Interface
 */
export interface OnboardingProgressTrackerRef {
  track: (event: string, data?: Record<string, any>) => void;
}

/**
 * Onboarding Progress Tracker Component
 *
 * Tracks onboarding events to GA4 analytics.
 * Provides imperative handle for external tracking calls.
 */
export const OnboardingProgressTracker = forwardRef<
  OnboardingProgressTrackerRef,
  OnboardingProgressTrackerProps
>(({ tourName = "default", userRole }, ref) => {
  const { user } = useAuth();
  const currentUserRole = userRole || user?.role || "unknown";
  const trackingDataRef = useRef<Record<string, any>>({});

  /**
   * Track an analytics event
   */
  const track = React.useCallback(
    (event: string, data?: Record<string, any>) => {
      try {
        const analyticsEvent: TourAnalyticsEvent = {
          action: event,
          category: "onboarding",
          label: tourName,
          custom_parameters: {
            tour_name: tourName,
            user_role: currentUserRole,
            ...trackingDataRef.current,
            ...data,
          },
        };

        // Add specific event data
        if (data?.stepName) {
          analyticsEvent.custom_parameters.step_name = data.stepName;
        }
        if (data?.stepIndex !== undefined) {
          analyticsEvent.custom_parameters.step_index = data.stepIndex;
          analyticsEvent.value = data.stepIndex + 1;
        }
        if (data?.timeSpent) {
          analyticsEvent.custom_parameters.time_spent = data.timeSpent;
        }
        if (data?.flowId) {
          analyticsEvent.custom_parameters.flow_id = data.flowId;
        }
        if (data?.status) {
          analyticsEvent.label = `${tourName} - ${data.status}`;
        }

        // Track event via GA4
        trackEvent(analyticsEvent);

        if (shouldLogOnboardingDebug) {
          logger.info("Onboarding event tracked", {
            event,
            tourName,
            userRole: currentUserRole,
            data,
          });
        }
      } catch (error) {
        logger.error("Error tracking onboarding event", {
          error,
          event,
          tourName,
          data,
        });
      }
    },
    [tourName, currentUserRole],
  );

  /**
   * Expose tracking method via ref
   */
  useImperativeHandle(
    ref,
    () => ({
      track,
    }),
    [track],
  );

  // This component doesn't render anything
  return null;
});

OnboardingProgressTracker.displayName = "OnboardingProgressTracker";

export default OnboardingProgressTracker;
